import {
  Button,
  createStyles,
  Group,
  Modal,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { MIME_TYPES } from '@mantine/dropzone';
import { useForm, yupResolver } from '@mantine/form';
import React from 'react';
import { object, string } from 'yup';

import {
  DeviceWarrantyType,
  useCreateDeviceWarranty,
  useUpdateDeviceWarranty,
} from '@portals/api/organizations';
import { ModalFooter } from '@portals/core';
import { ModalProps } from '@portals/framework';
import { ReactComponent as ArrowRight } from '@portals/icons/linear/arrow-right.svg';
import { ReactComponent as Calendar } from '@portals/icons/linear/calendar.svg';

import { FileUploadSwitcher } from './FileUploadSwitcher';

export interface UploadDeviceWarrantyModalData {
  deviceId: string;
  warrantyToEdit?: DeviceWarrantyType;
}

interface AddWarrantyProps extends ModalProps<UploadDeviceWarrantyModalData> {}

const schema = object({
  fileUrl: string().url('Invalid URL').required('File is required'),
  name: string().required('Name is required'),
  startDate: string().required('Start date is required'),
  endDate: string().required('End date is required'),
});

interface UploadDeviceWarrantyFormValues {
  name: string;
  description: string;
  startDate?: Date;
  endDate?: Date;
  fileUrl: string;
}

export function UploadDeviceWarrantyModal({
  closeMe,
  data: { deviceId, warrantyToEdit },
}: AddWarrantyProps) {
  const { classes } = useStyles();
  const createDeviceWarranty = useCreateDeviceWarranty(deviceId);
  const updateDeviceWarranty = useUpdateDeviceWarranty(deviceId);

  const isEditMode = warrantyToEdit !== undefined;

  const form = useForm<UploadDeviceWarrantyFormValues>({
    initialValues: {
      name: isEditMode ? warrantyToEdit.name : '',
      description: warrantyToEdit?.description || '',
      startDate: isEditMode ? new Date(warrantyToEdit.start_date) : undefined,
      endDate: isEditMode ? new Date(warrantyToEdit.end_date) : undefined,
      fileUrl: warrantyToEdit?.warranty_document_url ?? '',
    },
    validate: yupResolver(schema),
  });

  const submitHandler = ({
    name,
    startDate,
    endDate,
    description,
    fileUrl,
  }: typeof form.values) => {
    if (!startDate || !endDate) return;

    if (isEditMode) {
      const updatedWarranty: DeviceWarrantyType = {
        ...warrantyToEdit,
        name,
        description,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),

        warranty_document_url: fileUrl,
      };

      updateDeviceWarranty.mutate(updatedWarranty, {
        onSuccess: () => closeMe(),
      });
    } else {
      createDeviceWarranty.mutate(
        {
          name,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          description,
          warranty_document_url: fileUrl,
        },
        {
          onSuccess: () => closeMe(),
        }
      );
    }
  };

  return (
    <Modal
      opened
      size={680}
      padding="xxl"
      onClose={closeMe}
      title={isEditMode ? 'Update Warranty' : 'Upload Warranty'}
    >
      <form noValidate onSubmit={form.onSubmit(submitHandler)}>
        <Stack spacing="xl">
          <FileUploadSwitcher
            initialFileUrl={warrantyToEdit?.warranty_document_url}
            onChangeFileUrl={(fileUrl) =>
              form.setFieldValue('fileUrl', fileUrl)
            }
            fileUrlError={form.errors.fileUrl}
            data-testid="file-upload-switcher"
            acceptFileTypes={[MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png]}
          />

          <TextInput
            required
            label="Name"
            placeholder="Warranty name"
            {...form.getInputProps('name')}
            data-testid="input-warranty-name"
          />

          <Group align="flex-start">
            <DatePickerInput
              required
              label="Start date"
              placeholder="Start date"
              maxDate={form.values.endDate}
              icon={<Calendar width={18} height={18} />}
              sx={{ flexGrow: 1 }}
              popoverProps={{ withinPortal: true }}
              {...form.getInputProps('startDate')}
              data-testid="date-picker-input-warranty-start"
            />

            <ArrowRight
              className={classes.arrowContainer}
              width={16}
              height={16}
            />

            <DatePickerInput
              required
              label="End date"
              placeholder="End date"
              minDate={form.values.startDate}
              icon={<Calendar width={18} height={18} />}
              sx={{ flexGrow: 1 }}
              popoverProps={{ withinPortal: true }}
              {...form.getInputProps('endDate')}
              data-testid="date-picker-input-warranty-end"
            />
          </Group>

          <Textarea
            maxRows={3}
            minRows={3}
            label="Short description (optional)"
            placeholder="Short description (optional)"
            {...form.getInputProps('description')}
            data-testid="input-warranty-description"
          />

          <ModalFooter position="right">
            <Button variant="default" onClick={closeMe}>
              Cancel
            </Button>

            <Button
              type="submit"
              loading={
                createDeviceWarranty.isLoading || updateDeviceWarranty.isLoading
              }
              data-testid="add-warranty-submit-button"
            >
              {isEditMode ? 'Update Warranty' : 'Add Warranty'}
            </Button>
          </ModalFooter>
        </Stack>
      </form>
    </Modal>
  );
}

const useStyles = createStyles({
  arrowContainer: {
    marginTop: '6.2%', // using percentage in order handle errors space
  },
});
